import React, { useEffect, useRef } from "react";
import type { UploadConfig } from "../../domain/entities/UploadConfig.js";
import type { UploadItem } from "../../domain/entities/UploadItem.js";
import { useUploadController } from "../hooks/useUploadController.js";
import { UploadItemTile } from "./UploadItemTile.js";
import { UploadPickerButton } from "./UploadPickerButton.js";

interface Props {
  config: UploadConfig;
  initialUrls?: string[];
  onChange?: (items: UploadItem[], uploadedUrls: string[]) => void;
  itemSize?: number;
  /** Optional explicit tile width; defaults to `itemSize` (square), matching prior behavior. */
  itemWidth?: number;
  /** Optional explicit preview kind for tiles. Omit to keep the original image-or-icon sniffing. */
  kind?: "image" | "video" | "audio" | "file";
}

export function UploadGridWidget({
  config,
  initialUrls,
  onChange,
  itemSize = 100,
  itemWidth,
  kind,
}: Props) {
  const { items, pickAndUpload, setFromUrls, removeItem, retry, uploadedUrls } =
    useUploadController(config);

  // Seed the grid from the field's existing value (edit mode). `initialUrls` can
  // arrive AFTER mount when the submission query resolves later than the fields
  // query (the two run in parallel, unordered), so a one-shot mount effect would
  // miss it and open the field empty despite stored files. Re-sync when
  // `initialUrls` becomes available, but exactly once and only while the grid is
  // still empty, so we never clobber uploads the user started in the meantime.
  const seededRef = useRef(false);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  useEffect(() => {
    if (seededRef.current) return;
    if (!initialUrls?.length) return;
    if (itemsRef.current.length > 0) return;
    seededRef.current = true;
    setFromUrls(initialUrls);
  }, [initialUrls, setFromUrls]);

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    onChangeRef.current?.(items, uploadedUrls);
    // Deliberately excludes onChange: an inline (non-memoized) handler would
    // otherwise re-fire this on every render if it triggers a parent update.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [items, uploadedUrls]);

  const maxFiles = config.maxFiles ?? 0;
  const showAddButton = maxFiles === 0 || items.length < maxFiles;

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "flex-start" }}>
      {items.map((item) => (
        <UploadItemTile
          key={item.id}
          item={item}
          onRemove={removeItem}
          onRetry={retry}
          size={itemSize}
          width={itemWidth}
          kind={kind}
        />
      ))}
      {showAddButton && (
        <UploadPickerButton config={config} onFiles={pickAndUpload} size={itemSize} />
      )}
    </div>
  );
}
